import { useEffect, useRef, useState } from 'react';
import { ApiError } from '../../api/client';
import { useSetMemberTitle, useTeamMembers } from '../../api/hooks';
import type { TeamMember } from '../../types';
import { darkTheme, FONT } from '../../theme/tokens';
import { Spinner } from '../ui/Spinner';
import { ROLE_LABELS } from './roleLabels';

const t = darkTheme;

// Mirrors the BE `SetMemberTitleRequest.title` max_length.
const TITLE_MAX = 120;

interface TeamMembersListProps {
  /** The viewer's own membership id — their row is marked "You". */
  currentMembershipId: string | null;
  /** EDIT_MEMBER_TITLE — shows the inline title editor on every row. */
  canEditTitle: boolean;
}

// SC-3227 — the org's roster, visible to every active member. People who have
// JOINED; pending invitations are a separate, owner-only list below it.
export function TeamMembersList({ currentMembershipId, canEditTitle }: TeamMembersListProps) {
  const { data, isLoading, isError, error, refetch } = useTeamMembers();
  const setTitle = useSetMemberTitle();
  const [editingId, setEditingId] = useState<string | null>(null);
  // The row whose editor just closed — its Edit button takes focus back, so a
  // keyboard user isn't dropped to <body> after Save / Cancel / Escape.
  const [returnFocusId, setReturnFocusId] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
        <Spinner size={24} />
      </div>
    );
  }

  // Only a network failure or a 5xx can change on refetch; a 4xx (no org
  // scope, forbidden) would fail identically, so it gets no Retry.
  const canRetry = !(error instanceof ApiError) || error.status >= 500;

  // No roster at all → the error replaces the table. A failed BACKGROUND
  // refetch keeps its cached rows (and any open title draft) on screen, with
  // a warning above them instead.
  if (!data) {
    return (
      <div
        role="alert"
        style={{
          marginBottom: 28,
          padding: '12px 16px',
          background: t.redBg,
          border: `1px solid ${t.red}`,
          borderRadius: 8,
          color: t.redText,
          fontSize: 13,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <span style={{ flex: 1 }}>
          Couldn&rsquo;t load your team.{' '}
          {error instanceof ApiError ? error.detail : 'Please try again.'}
        </span>
        {canRetry && (
          <button
            type="button"
            onClick={() => void refetch()}
            style={{
              padding: '6px 12px',
              borderRadius: 6,
              border: `1px solid ${t.redBorder}`,
              background: 'transparent',
              color: t.redText,
              fontSize: 12.5,
              fontWeight: 600,
              fontFamily: FONT,
              cursor: 'pointer',
            }}
          >
            Retry
          </button>
        )}
      </div>
    );
  }

  const { members } = data;

  const startEdit = (id: string) => {
    // A failed save's error belongs to the row it was for; opening another
    // editor starts clean.
    setTitle.reset();
    setReturnFocusId(null);
    setEditingId(id);
  };

  const closeEditor = (id: string) => {
    setEditingId(null);
    setReturnFocusId(id);
  };

  const saveTitle = (member: TeamMember, draft: string) => {
    const title = draft.trim() || null;
    // Compared normalized on both sides: an untouched draft of a stored title
    // with stray whitespace is still "no edit".
    if (title === (member.title?.trim() || null)) {
      closeEditor(member.membership_id);
      return;
    }
    setTitle.mutate(
      { membershipId: member.membership_id, title },
      { onSuccess: () => closeEditor(member.membership_id) },
    );
  };

  return (
    <section style={{ marginBottom: 28 }}>
      <h2 style={{ fontSize: 15, fontWeight: 600, color: t.text, margin: '0 0 12px' }}>
        Members <span style={{ color: t.text3, fontWeight: 500 }}>· {members.length}</span>
      </h2>

      {isError && (
        <div
          role="alert"
          style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, fontSize: 12.5, color: t.redText }}
        >
          <span>
            Couldn&rsquo;t refresh your team; showing the last loaded list.{' '}
            {error instanceof ApiError ? error.detail : ''}
          </span>
          {canRetry && (
            <button
              type="button"
              onClick={() => void refetch()}
              style={{
                padding: '3px 10px',
                borderRadius: 6,
                border: `1px solid ${t.redBorder}`,
                background: 'transparent',
                color: t.redText,
                fontSize: 12,
                fontWeight: 600,
                fontFamily: FONT,
                cursor: 'pointer',
              }}
            >
              Retry
            </button>
          )}
        </div>
      )}

      <div style={{ border: `1px solid ${t.border}`, borderRadius: 10, overflow: 'hidden' }}>
        {/* Fixed layout: opening the inline title editor must not reflow the
            other columns (auto layout re-wraps names and emails mid-edit). */}
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '24%' }} />
            <col style={{ width: '32%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '32%' }} />
          </colgroup>
          <thead>
            <tr style={{ background: t.surfaceAlt }}>
              <Th>Name</Th>
              <Th>Title</Th>
              <Th>Role</Th>
              <Th>Email</Th>
            </tr>
          </thead>
          <tbody>
            {members.map((member) => (
              <tr key={member.membership_id} style={{ borderTop: `1px solid ${t.border}` }}>
                <Td style={{ color: t.text, fontWeight: 500 }}>
                  {member.name}
                  {member.membership_id === currentMembershipId && <YouChip />}
                </Td>
                <Td>
                  {editingId === member.membership_id ? (
                    <TitleEditor
                      member={member}
                      saving={setTitle.isPending}
                      error={setTitle.error}
                      onSave={(draft) => saveTitle(member, draft)}
                      onCancel={() => closeEditor(member.membership_id)}
                    />
                  ) : (
                    <TitleDisplay
                      member={member}
                      canEdit={canEditTitle}
                      // One editor at a time; don't open another mid-save.
                      disabled={setTitle.isPending}
                      focusOnMount={returnFocusId === member.membership_id}
                      onEdit={() => startEdit(member.membership_id)}
                    />
                  )}
                </Td>
                <Td>{ROLE_LABELS[member.role] ?? member.role}</Td>
                {/* `||` not `??`: an FE deployed ahead of the BE's email field
                    reads it as undefined — show a dash, not a blank cell. */}
                <Td>{member.email || '—'}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function YouChip() {
  return (
    <span
      style={{
        marginLeft: 8,
        padding: '1px 7px',
        borderRadius: 999,
        background: t.accentBg,
        color: t.accentText,
        fontSize: 11,
        fontWeight: 600,
      }}
    >
      You
    </span>
  );
}

function TitleDisplay({
  member,
  canEdit,
  disabled,
  focusOnMount,
  onEdit,
}: {
  member: TeamMember;
  canEdit: boolean;
  disabled: boolean;
  focusOnMount: boolean;
  onEdit: () => void;
}) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  // Mounts in place of a just-closed editor — take focus back from it.
  useEffect(() => {
    if (focusOnMount) buttonRef.current?.focus();
  }, [focusOnMount]);

  if (!canEdit) {
    return <span style={{ color: member.title ? t.text2 : t.text3 }}>{member.title ?? '—'}</span>;
  }
  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={onEdit}
      disabled={disabled}
      // The visible text leads the name too (WCAG 2.5.3 label-in-name), so
      // voice control can target "Head of Finance" or "Add title".
      aria-label={
        member.title
          ? `Edit title for ${member.name}: ${member.title}`
          : `Add title for ${member.name}`
      }
      style={{
        padding: 0,
        border: 'none',
        background: 'transparent',
        fontFamily: FONT,
        fontSize: 13,
        textAlign: 'left',
        // Dimmed while another row saves, so the lock reads before a click.
        color: disabled ? t.text3 : member.title ? t.text2 : t.accentText,
        cursor: disabled ? 'default' : 'pointer',
      }}
    >
      {member.title ? (
        <>
          {member.title} <span style={{ color: t.text3 }}>✎</span>
        </>
      ) : (
        '+ Add title'
      )}
    </button>
  );
}

function TitleEditor({
  member,
  saving,
  error,
  onSave,
  onCancel,
}: {
  member: TeamMember;
  saving: boolean;
  error: Error | null;
  onSave: (draft: string) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState(member.title ?? '');

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSave(draft);
      }}
      style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
    >
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.stopPropagation();
              onCancel();
            }
          }}
          maxLength={TITLE_MAX}
          disabled={saving}
          placeholder="e.g. Head of Finance"
          aria-label={`Title for ${member.name}`}
          style={{
            flex: 1,
            minWidth: 0,
            padding: '5px 8px',
            borderRadius: 6,
            border: `1px solid ${t.border2}`,
            background: t.surfaceDepressed,
            color: t.text,
            fontSize: 13,
            fontFamily: FONT,
          }}
        />
        <button type="submit" disabled={saving} style={editorButton(t.accentText, saving)}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button type="button" onClick={onCancel} disabled={saving} style={editorButton(t.text2, saving)}>
          Cancel
        </button>
      </div>
      {error && (
        <div role="alert" style={{ fontSize: 12, color: t.redText }}>
          Couldn&rsquo;t save the title.{' '}
          {error instanceof ApiError ? error.detail : 'Please try again.'}
        </div>
      )}
    </form>
  );
}

function editorButton(color: string, disabled: boolean): React.CSSProperties {
  return {
    padding: '5px 10px',
    borderRadius: 6,
    border: `1px solid ${t.border2}`,
    background: 'transparent',
    color: disabled ? t.text3 : color,
    fontSize: 12.5,
    fontWeight: 600,
    fontFamily: FONT,
    cursor: disabled ? 'default' : 'pointer',
    whiteSpace: 'nowrap',
  };
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      style={{
        textAlign: 'left',
        padding: '10px 14px',
        fontSize: 11.5,
        fontWeight: 600,
        color: t.text3,
        textTransform: 'uppercase',
        letterSpacing: 0.4,
      }}
    >
      {children}
    </th>
  );
}

function Td({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  // `anywhere`: under the fixed layout an unbroken name, title or email must
  // wrap inside its column rather than run into the next one.
  return (
    <td style={{ padding: '11px 14px', color: t.text2, verticalAlign: 'top', overflowWrap: 'anywhere', ...style }}>
      {children}
    </td>
  );
}
